import { FC } from 'react';
import { Button, Card, Flex, Progress, Typography } from 'antd';
import { TbRefresh, TbTrash, TbX } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import OverflowFadeScrollArea from '@/shared/components/layouts/OverflowFadeScrollArea';

import AttachmentFileAvatar from '../../../AttachmentFileAvatar';
import { AttachmentUploadItem } from '../../useAttachmentBatchUpload';
import { formatAttachmentFileSize } from '../../useAttachmentFilePresentation';

const { Text } = Typography;

interface AttachmentUploadQueueProps {
    items: AttachmentUploadItem[];
    onCancel: (id: string) => void;
    onRetry: (id: string) => void;
}

const getUploadedBytes = (item: AttachmentUploadItem) => Math.round(item.size * (item.progress / 100));

const AttachmentUploadQueue: FC<AttachmentUploadQueueProps> = ({ items, onCancel, onRetry }) => {
    const { token } = useAntDesignTheme();

    if (!items.length) {
        return null;
    }

    return (
        <OverflowFadeScrollArea
            aria-label='Attachment upload queue'
            containerStyle={{
                borderTop: `1px solid ${token.colorBorder}`,
                flex: 'none',
                maxHeight: '18rem',
            }}
            style={{ padding: '0.75rem 1rem 1rem' }}
            surface='container'
        >
            <Flex gap='0.75rem' vertical>
                {items.map((item) => {
                    const isError = item.status === 'error';
                    const isProcessing = item.status === 'processing';
                    const statusColor = isError
                        ? token.colorError
                        : isProcessing
                          ? token.colorTextSecondary
                          : token.colorInfo;
                    const statusLabel =
                        item.status === 'queued'
                            ? 'Preparing'
                            : item.status === 'uploading'
                              ? 'Uploading...'
                              : item.status === 'processing'
                                ? 'Securing and saving'
                                : (item.error ?? 'Upload failed');
                    const sizeLabel =
                        item.status === 'uploading'
                            ? `${formatAttachmentFileSize(getUploadedBytes(item))} of ${formatAttachmentFileSize(item.size)}`
                            : formatAttachmentFileSize(item.size);

                    return (
                        <Card key={item.id} size='small' styles={{ body: { padding: '0.75rem 1rem' } }}>
                            <Flex align='center' gap='0.75rem'>
                                <AttachmentFileAvatar mimeType={item.mimeType} />
                                <Flex style={{ flex: '1 1 auto', minWidth: 0 }} vertical>
                                    <Text ellipsis title={item.relativePath || item.fileName}>
                                        {item.relativePath || item.fileName}
                                    </Text>
                                    <Text style={{ color: token.colorTextTertiary }} type='secondary'>
                                        {sizeLabel} · <span style={{ color: statusColor }}>{statusLabel}</span>
                                    </Text>
                                </Flex>
                                {isError ? (
                                    <Flex gap='0.25rem'>
                                        <Button
                                            aria-label={`Retry ${item.fileName}`}
                                            icon={<TbRefresh />}
                                            onClick={() => onRetry(item.id)}
                                            size='small'
                                        />
                                        <Button
                                            aria-label={`Remove ${item.fileName}`}
                                            icon={<TbTrash />}
                                            onClick={() => onCancel(item.id)}
                                            size='small'
                                        />
                                    </Flex>
                                ) : (
                                    <Button
                                        aria-label={`Cancel ${item.fileName}`}
                                        icon={<TbX />}
                                        onClick={() => onCancel(item.id)}
                                        size='small'
                                        type='text'
                                    />
                                )}
                            </Flex>
                            <Progress
                                percent={item.progress}
                                showInfo={false}
                                size='small'
                                status={isError ? 'exception' : 'active'}
                                strokeColor={isProcessing ? token.colorSuccess : undefined}
                                style={{ marginBottom: 0, marginTop: '0.5rem' }}
                            />
                        </Card>
                    );
                })}
            </Flex>
        </OverflowFadeScrollArea>
    );
};

export default AttachmentUploadQueue;
